// The sidebar (D-171): at most seven links, with the pages a link groups shown as tabs on each of them. Every page keeps
// its own address, so links and bookmarks to it still work. A team of one (D-169) has no Team; the AI's pages show only
// with Coach or Autopilot.
import type { Role } from "./types";
import { isAdminRole, isManagerRole } from "./format";

export interface NavTab {
  to: string;
  label: string;
}

/** One sidebar link; with tabs, the link opens the first and is lit on any of them. */
export interface NavItem {
  to: string;
  label: string;
  tabs?: NavTab[];
}

export function navFor(o: { role: Role; solo: boolean; addOn: boolean; canReview: boolean }): NavItem[] {
  const manager = isManagerRole(o.role);
  const admin = isAdminRole(o.role);
  const own = (team: string, mine: string) => (manager ? team : mine);
  const items: NavItem[] = [
    { to: "/overview", label: o.solo ? "My progress" : "Overview" },
    { to: "/today", label: own("Follow-ups", "My follow-ups") },
    { to: "/leads", label: own("Customers", "My customers") },
    { to: "/pipeline", label: own("Pipeline", "My pipeline") },
  ];
  if (manager) items.push({ to: "/calls", label: "Calls" });

  // Call review is the reviewers' panel (D-112): a team of one has no one else to review for.
  const review = o.canReview && o.addOn && !o.solo;
  if (manager && !o.solo) {
    items.push({
      to: "/team",
      label: "Team",
      tabs: [
        { to: "/team", label: "People" },
        { to: "/health", label: "Phones" },
        ...(review ? [{ to: "/review", label: "Call review" }] : []),
        { to: "/digests", label: "Morning digest" },
      ],
    });
  } else if (review) {
    items.push({ to: "/review", label: "Review calls" });
  }

  if (admin) {
    items.push({
      to: "/settings",
      label: "Settings",
      tabs: [
        { to: "/settings", label: "Company" },
        ...(o.solo ? [{ to: "/health", label: "Your phone" }] : []),
        { to: "/integrations", label: "Integrations" },
        { to: "/import", label: "Import leads" },
        ...(o.addOn ? [{ to: "/ai", label: "AI instructions" }, { to: "/call-notes", label: "Call notes" }] : []),
        { to: "/usage", label: "Minutes and billing" },
      ],
    });
  } else if (manager) {
    items.push({ to: "/import", label: "Import leads" });
  }
  return items;
}

/** The sidebar link a page belongs to: the one whose tabs hold it, or whose address it is or starts. */
export function navItemFor(items: NavItem[], path: string): NavItem | undefined {
  const under = (to: string) => path === to || path.startsWith(`${to}/`);
  return items.find((i) => i.tabs?.some((t) => under(t.to))) ?? items.find((i) => under(i.to));
}
